import {
  type WikiChangeSet,
  type WikiComment,
  type WikiThread,
  type WikiThreadKind,
  wikiCommentBodySchema,
} from "@esi/shared";
import {
  Bot,
  Check,
  CircleQuestionMark,
  MessageSquare,
  MessageSquareWarning,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "../i18n/format.ts";
import { navigate, seeChangePath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { textAreaClass } from "../tasks/parts.tsx";
import { ThreadEntries } from "./AgentReplies.tsx";
import { ResolveStep, useResolvedLine } from "./ResolveNote.tsx";
import { useThreads, useThreadWrite } from "./threads.ts";

const KIND_ICON: Record<WikiThreadKind, typeof MessageSquare> = {
  comment: MessageSquare,
  question: CircleQuestionMark,
  change_request: MessageSquareWarning,
};
const count = (text: string) => Array.from(text.normalize("NFC").trim()).length;

/**
 * The review threads of one anchor (wiki-collaboration D16): the change set itself
 * (`itemId` null) or one of its items. Task-comments anatomy: a divided hairline list of
 * threads, each with its comments (author, time, plain text), Resolve or Reopen, a reply
 * composer; then a composer for a new thread with its kind. Only what the server returns is
 * shown, so a hidden item's thread never reaches this view.
 */
export function ThreadPanel({
  projectId,
  set,
  itemId,
  canComment,
}: {
  projectId: string;
  set: Pick<WikiChangeSet, "id" | "status" | "origin" | "provenance" | "createdBy">;
  itemId: string | null;
  /** `wiki.view_drafts` in an open project: may open threads and reply. */
  canComment: boolean;
}) {
  const { t } = useTranslation();
  const { userId } = useSession();
  const threads = useThreads(projectId, set.id);
  const write = useThreadWrite(projectId, set.id);
  const headingId = useId();
  const [remove, setRemove] = useState<{ threadId: string; commentId: string } | null>(null);
  const closed = set.status === "published" || set.status === "discarded";
  // An N-1 API has no threads route: the panel stays out of the way.
  if (threads.isError) {
    return null;
  }
  const mine = (threads.data ?? []).filter((thread) => thread.itemId === itemId);
  // The agent can revise an item of a set it wrote in a conversation (D15).
  // Its conversation may be hidden from a reviewer: the origin says whether there is one.
  // The set's creator talks to their agent in the conversation itself (D15).
  const agentEligible =
    itemId !== null &&
    set.origin === "agent" &&
    !set.provenance.conversationDeleted &&
    set.createdBy.toLowerCase() !== userId.toLowerCase();
  return (
    <section data-esi-content="wiki" aria-labelledby={headingId} className="flex flex-col gap-3">
      <h4 id={headingId} className="text-xs font-medium text-fg-muted">
        {itemId ? t("wiki.threads.item") : t("wiki.threads.set")}
      </h4>
      {threads.isPending ? (
        <Skeleton className="h-16" />
      ) : mine.length ? (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
          {mine.map((thread) => (
            <ThreadView
              key={thread.id}
              thread={thread}
              busy={write.isPending}
              onReply={(body) => write.mutateAsync({ kind: "reply", threadId: thread.id, body })}
              onResolve={(resolved, note) =>
                write.mutateAsync(
                  resolved
                    ? { kind: "resolve", threadId: thread.id, note }
                    : { kind: "reopen", threadId: thread.id },
                )
              }
              onDelete={(commentId) => setRemove({ threadId: thread.id, commentId })}
              onSeeChange={() =>
                navigate(seeChangePath(projectId, set.id, thread.itemId, thread.id))
              }
              meta={
                thread.anchor?.quote ? (
                  // The passage the thread was opened on (wiki-collaboration-ux D2), as the
                  // decider's pane quotes it: tenant content.
                  <p data-esi-content="wiki" className="text-xs break-words text-fg-muted">
                    {t("wiki.review.pane.on")} “
                    <span className="text-fg-secondary">{thread.anchor.quote}</span>”
                  </p>
                ) : null
              }
            />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-fg-muted">{t("wiki.threads.none")}</p>
      )}
      {closed ? (
        <p className="text-xs text-fg-muted">{t("wiki.threads.readOnly")}</p>
      ) : canComment ? (
        <NewThread
          key={itemId ?? "set"}
          label={itemId ? t("wiki.threads.bodyItem") : t("wiki.threads.bodySet")}
          agentEligible={agentEligible}
          busy={write.isPending}
          onPost={(kind, body, forAgent) =>
            write.mutateAsync({
              kind: "open",
              request: {
                kind,
                body,
                ...(itemId && { itemId }),
                ...(forAgent && { forAgent: true }),
              },
            })
          }
        />
      ) : null}
      {write.isError && !remove ? <InlineError>{write.error.message}</InlineError> : null}
      {remove ? (
        <DeleteCommentDialog
          busy={write.isPending}
          error={write.isError ? write.error.message : null}
          onClose={() => setRemove(null)}
          onDelete={() => write.mutateAsync({ kind: "delete", ...remove })}
        />
      ) : null}
    </section>
  );
}

/** Asks to confirm a comment's deletion; it stays open with the refusal if one comes back. */
export function DeleteCommentDialog({
  busy,
  error,
  onClose,
  onDelete,
}: {
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onDelete: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      open
      onClose={() => {
        if (!busy) {
          onClose();
        }
      }}
      title={t("wiki.threads.deleteComment")}
      description={t("wiki.threads.deleteBody")}
    >
      <DialogFooter>
        <Button variant="outline" disabled={busy} onClick={onClose}>
          {t("app.cancel")}
        </Button>
        <Button
          variant="destructive"
          className="text-accent-on dark:bg-destructive dark:hover:bg-destructive/90"
          disabled={busy}
          onClick={async () => {
            try {
              await onDelete();
              onClose();
            } catch {
              // Shown in the dialog.
            }
          }}
        >
          {t("wiki.threads.deleteComment")}
        </Button>
      </DialogFooter>
      {error ? <InlineError>{error}</InlineError> : null}
    </AppDialog>
  );
}

/** What a thread view needs: review threads (D16) and page threads (D17) alike. */
export type ThreadLike = Pick<
  WikiThread,
  "kind" | "status" | "resolvedByName" | "resolutionNote" | "mayResolve" | "mayReply" | "comments"
> & {
  forAgent?: boolean;
  /** The thread's author, whom an agent question asks (wiki-resolve-with-agent D6). */
  createdBy?: string;
  createdByName?: string | null;
  agentReplies?: WikiThread["agentReplies"];
};

export function ThreadView({
  thread,
  busy,
  onReply,
  onResolve,
  onDelete,
  onSeeChange,
  meta,
  footer,
}: {
  thread: ThreadLike;
  /** "See the change" of a Changed agent reply: the passage's change in the review. */
  onSeeChange?: () => void;
  busy: boolean;
  onReply: (body: string) => Promise<unknown>;
  /** Resolve with the optional decision note (D16), or reopen. */
  onResolve: (resolved: boolean, note?: string) => Promise<unknown>;
  onDelete: (commentId: string) => void;
  /** A line under the thread's kind: where a page thread sits (D17). */
  meta?: React.ReactNode;
  /** Actions after the comments: "Ask the agent" on a page thread (D17). */
  footer?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [replying, setReplying] = useState(false);
  const [resolving, setResolving] = useState(false);
  const resolvedLine = useResolvedLine();
  const Icon = KIND_ICON[thread.kind];
  const resolved = thread.status === "resolved";
  // An open change request blocks decisions: its mark is a state signal (amber), never colour
  // alone — the kind is always written.
  const blocking = thread.kind === "change_request" && !resolved;
  return (
    <li className="flex flex-col gap-3 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <span
            className={`flex items-center gap-1.5 font-medium ${blocking ? "text-warning-fg" : "text-fg-secondary"}`}
          >
            <Icon aria-hidden="true" className="size-3.5 shrink-0" />
            {t(`wiki.threads.kind.${thread.kind}`)}
          </span>
          {thread.forAgent ? (
            <span className="flex items-center gap-1 text-fg-muted">
              · <Bot aria-hidden="true" className="size-3.5" />
              {t("wiki.threads.askedAgent")}
            </span>
          ) : null}
          {resolved ? (
            <span data-esi-content="wiki" className="break-words text-fg-muted">
              · {resolvedLine(thread)}
            </span>
          ) : null}
        </p>
        {thread.mayResolve && !resolving ? (
          <Button
            size="xs"
            variant="ghost"
            disabled={busy}
            onClick={() => (resolved ? void onResolve(false).catch(() => {}) : setResolving(true))}
          >
            {resolved ? <RotateCcw aria-hidden="true" /> : <Check aria-hidden="true" />}
            {resolved ? t("wiki.threads.reopen") : t("wiki.threads.resolve")}
          </Button>
        ) : null}
      </div>
      {resolving && !resolved ? (
        <ResolveStep
          busy={busy}
          onCancel={() => setResolving(false)}
          onResolve={async (note) => {
            await onResolve(true, note);
            setResolving(false);
          }}
        />
      ) : null}
      {meta}
      <ThreadEntries
        comments={thread.comments}
        agentReplies={thread.agentReplies}
        reviewer={{ userId: thread.createdBy ?? "", name: thread.createdByName ?? null }}
        mayReply={thread.mayReply}
        busy={busy}
        onPick={(choice) => void onReply(choice).catch(() => {})}
        onAnswer={() => setReplying(true)}
        onSeeChange={onSeeChange ? () => onSeeChange() : null}
        comment={(comment) => (
          <CommentView key={comment.id} comment={comment} busy={busy} onDelete={onDelete} />
        )}
      />
      {footer}
      {thread.mayReply ? (
        replying ? (
          <Composer
            label={t("wiki.threads.replyLabel")}
            submit={t("wiki.threads.reply")}
            busy={busy}
            onCancel={() => setReplying(false)}
            onPost={async (body) => {
              await onReply(body);
              setReplying(false);
            }}
          />
        ) : (
          <div>
            <Button size="xs" variant="ghost" onClick={() => setReplying(true)}>
              {t("wiki.threads.reply")}
            </Button>
          </div>
        )
      ) : null}
    </li>
  );
}

export function CommentView({
  comment,
  busy,
  onDelete,
}: {
  comment: WikiComment;
  busy: boolean;
  onDelete: (commentId: string) => void;
}) {
  const { t } = useTranslation();
  if (comment.system === "agent_revised") {
    return (
      <li className="flex items-center gap-1.5 text-xs text-fg-muted">
        <Bot aria-hidden="true" className="size-3.5" />
        <span>{t("wiki.threads.agentRevised")}</span>
        <time dateTime={comment.createdAt}>· {formatDateTime(comment.createdAt)}</time>
      </li>
    );
  }
  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 text-sm text-fg-secondary">
          <span className="break-words font-medium">
            {comment.authorName ?? t("wiki.threads.someone")}
          </span>
          <time className="ml-3 text-xs text-fg-muted" dateTime={comment.createdAt}>
            {formatDateTime(comment.createdAt)}
          </time>
        </p>
        {comment.mayDelete ? (
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={busy}
            aria-label={t("wiki.threads.deleteComment")}
            onClick={() => onDelete(comment.id)}
          >
            <Trash2 aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {comment.deleted ? (
        <p className="text-sm italic text-fg-muted">{t("wiki.threads.deleted")}</p>
      ) : (
        <p className="text-sm whitespace-pre-wrap break-words text-fg">{comment.body}</p>
      )}
    </li>
  );
}

export function Composer({
  label,
  submit,
  busy,
  onPost,
  onCancel,
  children,
}: {
  label: string;
  submit: string;
  busy: boolean;
  onPost: (body: string) => Promise<unknown>;
  onCancel?: () => void;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        const parsed = wikiCommentBodySchema.safeParse(body);
        if (!parsed.success) {
          setError(t("wiki.threads.invalid"));
          return;
        }
        setError(null);
        try {
          await onPost(parsed.data);
          setBody("");
        } catch {
          // The panel shows the refusal.
        }
      }}
    >
      {children}
      <Label htmlFor={`${id}-body`} className="text-sm font-medium">
        {label}
      </Label>
      <textarea
        id={`${id}-body`}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className={`${textAreaClass} min-h-20`}
        disabled={busy}
        aria-describedby={`${id}-count`}
        aria-invalid={error ? true : undefined}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p id={`${id}-count`} className="text-xs text-fg-muted tabular-nums">
          {count(body)}/2000
        </p>
        <div className="flex gap-2">
          {onCancel ? (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
              {t("app.cancel")}
            </Button>
          ) : null}
          <Button type="submit" size="sm" variant="outline" disabled={busy}>
            <MessageSquare aria-hidden="true" />
            {submit}
          </Button>
        </div>
      </div>
      {error ? <InlineError>{error}</InlineError> : null}
    </form>
  );
}

export function NewThread({
  label,
  agentEligible,
  busy,
  onPost,
}: {
  label: string;
  agentEligible: boolean;
  busy: boolean;
  onPost: (kind: WikiThreadKind, body: string, forAgent: boolean) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [kind, setKind] = useState<WikiThreadKind>("comment");
  const [forAgent, setForAgent] = useState(false);
  const askAgent = agentEligible && kind === "change_request";
  return (
    <Composer
      label={label}
      submit={t("wiki.threads.post")}
      busy={busy}
      onPost={async (body) => {
        await onPost(kind, body, askAgent && forAgent);
        // The next remark is a comment again: a thanks never requests changes (D12).
        setKind("comment");
        setForAgent(false);
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-kind`} className="text-sm font-medium">
          {t("wiki.threads.kindLabel")}
        </Label>
        <NativeSelect
          id={`${id}-kind`}
          value={kind}
          className="sm:w-56"
          disabled={busy}
          onChange={(e) => setKind(e.currentTarget.value as WikiThreadKind)}
        >
          {(["comment", "question", "change_request"] as const).map((k) => (
            <option key={k} value={k}>
              {t(`wiki.threads.kindOption.${k}`)}
            </option>
          ))}
        </NativeSelect>
      </div>
      {askAgent ? (
        <div className="flex items-start gap-2.5">
          <Checkbox
            id={`${id}-agent`}
            checked={forAgent}
            disabled={busy}
            aria-describedby={`${id}-agent-hint`}
            onCheckedChange={(on) => setForAgent(on === true)}
          />
          <div className="flex flex-col gap-0.5">
            <Label htmlFor={`${id}-agent`} className="font-normal">
              {t("wiki.threads.forAgent")}
            </Label>
            <p id={`${id}-agent-hint`} className="text-xs text-fg-muted">
              {t("wiki.threads.forAgentHint")}
            </p>
          </div>
        </div>
      ) : null}
    </Composer>
  );
}
